import i18n from '@/lang/i18n'
import { store } from '@/store/store'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'
import { ColDef } from '@ag-grid-enterprise/all-modules'
import { AgGridColumnType, AgGridExportType, ExportedColDef, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import { ICellRendererParams } from '@ag-grid-community/core/dist/cjs/rendering/cellRenderers/iCellRenderer'
import { ModuleOperationGuard } from '@/api/operations/guards/module-operation-guard'

const registryCaseGridChildrenColumnDefs = (): (ColDef | ExportedColDef | ExportedColGroupDef)[] => ([
    {
        headerName: i18n.tc('grid.detail.studyId'),
        field: 'casCasenumber',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        minWidth: 110,
        suppressSizeToFit: true
    },
    {
        headerName: i18n.tc('grid.casRegistrationDate'),
        field: 'casRegistrationDate',
        type: AgGridColumnType.DATETIME,
        exportType: AgGridExportType.DATETIME,
        minWidth: 150
    },
    {
        headerName: i18n.tc('entities.case.registrationResponsible'),
        colId: 'registerPersonnel.normalizedFullName',
        field: 'registrar.fullName',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        minWidth: 110,
        suppressSizeToFit: true
    },
    {
        headerName: i18n.tc('global.status'),
        field: 'status',
        type: AgGridColumnType.COMMON_CASE_STATUS,
        exportField: 'status.name',
        exportType: AgGridExportType.TEXT
    },
    {
        headerName: i18n.tc('grid.casValidationDate'),
        field: 'casValidationDate',
        type: [AgGridColumnType.DATETIME, 'fixedWidth'],
        exportType: AgGridExportType.DATETIME
    },
    {
        headerName: i18n.tc('global.priority'),
        colId: 'priority.level',
        field: 'priority',
        type: AgGridColumnType.PRIORITY,
        exportField: 'priority.name',
        exportType: AgGridExportType.TEXT
    },
    {
        headerName: i18n.tc('grid.detail.patName'),
        colId: 'pathologist.normalizedFullName',
        field: 'pathologist.fullName',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        width: 110,
        suppressSizeToFit: true
    },
    {
        headerName: i18n.tc('global.externalReference'),
        field: 'externalReference',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT
    }
])

export const registryCaseGridColumnDefs = (canShowAuthorizedColumn: boolean = false): (ColDef | ExportedColDef | ExportedColGroupDef)[] => ([
    { type: AgGridColumnType.CUSTOM_CHECKBOX },
    {
        headerName: i18n.tc('registry.requestGrid.contextMenu.case'),
        children: ModuleOperationGuard.canAccessBilling && canShowAuthorizedColumn ? registryCaseGridChildrenColumnDefs().concat([{
            type: AgGridColumnType.HAS_UNAUTHORIZED_BILLABLE_CONCEPTS,
            exportType: AgGridExportType.BOOLEAN,
            field: 'hasUnauthorizedBillableConcepts',
            flex: 1,
            minWidth: 55
        }]) : registryCaseGridChildrenColumnDefs()
    },
    {
        headerName: i18n.tc('global.patient'),
        children: [
            {
                headerValueGetter: () => store.getters.parameters.global?.mainPatientIdentifierType.name,
                colId: 'patient.personIdentifier',
                field: 'patient.mainPatientIdentifier.paiIdentifier',
                type: AgGridColumnType.TEXT,
                exportType: AgGridExportType.TEXT,
                sortable: false,
                filterParams: {
                    filterOptions: ['equals'],
                    defaultOption: 'equals',
                    newRowsAction: 'keep'
                }
            },
            {
                headerValueGetter: () => store.getters.parameters.global?.secondaryPatientIdentifierType.name,
                colId: 'patient.centerIdentifier',
                field: 'patient.secondaryPatientIdentifier.paiIdentifier',
                type: AgGridColumnType.TEXT,
                exportType: AgGridExportType.TEXT,
                sortable: false,
                filterParams: {
                    filterOptions: ['equals'],
                    defaultOption: 'equals',
                    newRowsAction: 'keep'
                }
            },
            {
                headerName: i18n.tc('grid.patName'),
                field: 'patient.patName',
                type: AgGridColumnType.TEXT,
                exportType: AgGridExportType.TEXT
            },
            {
                headerName: i18n.tc('grid.patMiddleName'),
                field: 'patient.patMiddleName',
                type: AgGridColumnType.TEXT,
                exportType: AgGridExportType.TEXT
            },
            {
                headerName: i18n.tc('grid.patSurname1'),
                field: 'patient.patSurname1',
                type: AgGridColumnType.TEXT,
                exportType: AgGridExportType.TEXT
            },
            {
                headerName: i18n.tc('grid.patSurname2'),
                field: 'patient.patSurname2',
                type: AgGridColumnType.TEXT,
                exportType: AgGridExportType.TEXT
            }
        ]
    },
    {
        headerName: i18n.tc('global.request'),
        children: [
            {
                headerName: i18n.tc('grid.headerName.requestLabel'),
                field: 'requestLabel',
                type: AgGridColumnType.TEXT,
                exportType: AgGridExportType.TEXT
            },
            {
                headerName: i18n.tc('grid.reqRequestDate'),
                field: 'casRequestDate',
                type: [AgGridColumnType.DATETIME, 'fixedWidth'],
                exportType: AgGridExportType.DATETIME
            },
            {
                headerName: i18n.tc('registry.cases.professional'),
                colId: 'physicianPetitioner.normalizedFullName',
                field: 'physicianPetitioner.fullName',
                type: AgGridColumnType.TEXT,
                exportType: AgGridExportType.TEXT,
                filterParams: {
                    filterOptions: ['contains'],
                    defaultOption: 'contains',
                    newRowsAction: 'keep'
                }
            },
            {
                headerName: i18n.tc('registry.cases.unit'),
                field: 'physicianDepartment.name',
                type: AgGridColumnType.DEPARTMENTS,
                exportType: AgGridExportType.TEXT
            },
            {
                headerName: i18n.tc('global.center'),
                field: 'physicianDepartment.center.name',
                type: AgGridColumnType.CENTERS,
                exportType: AgGridExportType.TEXT
            },
            {
                headerName: i18n.tc('global.automaticCreation'),
                headerTooltip: i18n.tc('global.automaticCreation'),
                headerComponentParams: {
                    template: buildIconHeaderTemplate('far fa-magic')
                },
                field: 'request.automaticallyCreated',
                type: AgGridColumnType.TEXT,
                exportType: AgGridExportType.BOOLEAN,
                cellRenderer: ({ value }: ICellRendererParams) => value ? '<i class="success far fa-check"/>' : '',
                hide: false,
                sortable: false,
                filter: false
            }
        ]
    },
    {
        headerName: i18n.tc('grid.hasAlarms'),
        headerTooltip: i18n.tc('grid.hasAlarms'),
        headerComponentParams: {
            template: buildIconHeaderTemplate('far fa-exclamation-triangle')
        },
        field: 'incidents',
        type: AgGridColumnType.INCIDENT,
        exportType: AgGridExportType.INTEGER
    },
    {
        headerName: i18n.tc('grid.attachments'),
        headerTooltip: i18n.tc('grid.attachments'),
        field: 'attachments',
        type: AgGridColumnType.ATTACHMENT_COUNT,
        exportType: AgGridExportType.INTEGER,
        headerComponentParams: {
            template: buildIconHeaderTemplate('far fa-paperclip')
        }
    },
    {
        headerName: i18n.tc('registry.btn.consultation'),
        headerTooltip: i18n.tc('registry.btn.consultation'),
        headerComponentParams: {
            template: buildIconHeaderTemplate('far fa-glasses')
        },
        field: 'caseOfReference',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.BOOLEAN,
        cellRenderer: ({ value }: ICellRendererParams) => value ? '<i class="success far fa-check"/>' : '',
        hide: false,
        filter: false
    }
])
